import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Autocomplete,
  TextField,
  Breadcrumbs,
  Link,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { accountingApi, n11Api } from '../../../config/apiClient';

// N11 Kategori Arayüzü
interface N11Category {
  id: string;
  name: string;
  fullPath?: string;
  level?: number;
  hasChildren?: boolean;
}

// Stok Ürünü Arayüzü (sadece gerekli alanlar)
interface InventoryItem {
  id: string;
  name: string;
  description?: string;
  stockCode: string;
  barcode?: string;
  quantity: number;
  salePrice: number;
  vatRate?: number;
  imageUrl?: string | null;
}

// Kargo Şablonu Arayüzü
interface ShipmentTemplate {
  id: string;
  name: string;
  platform: string;
  isDefault: boolean;
}

// Props Arayüzü
interface N11CategorySelectionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (categoryId: string, shipmentTemplate?: string) => void;
  item: InventoryItem | null;
  companyId: string | null;
  loading?: boolean;
}

const N11CategorySelectionModal: React.FC<N11CategorySelectionModalProps> = ({
  open,
  onClose,
  onConfirm,
  item,
  companyId,
  loading = false
}) => {
  // State değişkenleri
  const [categoryLoading, setCategoryLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<N11Category | null>(null);
  const [categories, setCategories] = useState<N11Category[]>([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState<string>('');
  const [categoryBreadcrumb, setCategoryBreadcrumb] = useState<N11Category[]>([]);
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);
  
  // Kargo şablonu state'leri
  const [shipmentTemplates, setShipmentTemplates] = useState<ShipmentTemplate[]>([]);
  const [selectedShipmentTemplate, setSelectedShipmentTemplate] = useState<string>('');
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);

  // Modal açıldığında form'u temizle ve ana kategorileri yükle
  useEffect(() => {
    if (open) {
      handleClearForm();
      loadMainCategories();
      loadShipmentTemplates();
    }
  }, [open]);

  // Form temizleme
  const handleClearForm = () => {
    setSelectedCategory(null);
    setCategories([]);
    setCategorySearchTerm('');
    setCategoryBreadcrumb([]);
    setCurrentParentId(null);
    setSelectedShipmentTemplate('');
    setError(null);
  };

  // Kargo şablonlarını SQL API'den yükle
  const loadShipmentTemplates = async () => {
    if (!companyId) return;

    setTemplatesLoading(true);
    try {
      console.log('🚚 [N11 CATEGORY SELECTION] SQL API\'den kargo şablonları yükleniyor...');

      const response = await accountingApi.get(`/api/shipmenttemplate/by-platform?platform=n11`);
      const result = response.data;
      
      if (result.success && result.data && result.data.length > 0) {
        const templates: ShipmentTemplate[] = result.data.map((t: any) => ({
          id: String(t.id),
          name: t.name,
          platform: t.platform,
          isDefault: t.isDefault
        }));
        
        setShipmentTemplates(templates);
        
        const defaultTemplate = templates.find(t => t.isDefault);
        setSelectedShipmentTemplate(defaultTemplate?.name || templates[0]?.name || 'BAREM');
        
        console.log('✅ [N11 CATEGORY SELECTION] SQL\'den kargo şablonları yüklendi:', templates.length);
      } else {
        console.log('⚠️ [N11 CATEGORY SELECTION] SQL\'de şablon bulunamadı, varsayılan BAREM kullanılacak');
        setShipmentTemplates([]);
        setSelectedShipmentTemplate('BAREM');
      }
    } catch (error) {
      console.error('❌ [N11 CATEGORY SELECTION] Kargo şablonu yükleme hatası:', error);
      setShipmentTemplates([]);
      setSelectedShipmentTemplate('BAREM');
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Ana kategorileri yükle
  const loadMainCategories = async () => {
    if (!companyId) return;

    setCategoryLoading(true);
    try {
      console.log('🏠 [N11 CATEGORY SELECTION] Ana kategoriler yükleniyor...');

      const response = await n11Api.post('/api/n11/products/search-categories', { 
        searchTerm: '', 
        parentId: null
      });

      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.error || 'Ana kategori yükleme işlemi başarısız');
      }

      console.log('✅ [N11 CATEGORY SELECTION] Ana kategoriler yüklendi:', data.categories?.length || 0);
      setCategories(data.categories || []);
      setCategoryBreadcrumb([]);
      setCurrentParentId(null);

    } catch (error) {
      console.error('❌ [N11 CATEGORY SELECTION] Ana kategori yükleme hatası:', error);
      setError('Ana kategoriler yüklenirken hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setCategoryLoading(false);
    }
  };

  // Alt kategorileri yükle
  const loadSubCategories = async (parentCategory: N11Category) => {
    if (!companyId) return;

    setCategoryLoading(true);
    try {
      console.log('🌳 [N11 CATEGORY SELECTION] Alt kategoriler yükleniyor...', parentCategory.name);

      const response = await n11Api.post('/api/n11/products/search-categories', { 
        searchTerm: '', 
        parentId: parentCategory.id
      });

      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.error || 'Alt kategori yükleme işlemi başarısız');
      }

      console.log('✅ [N11 CATEGORY SELECTION] Alt kategoriler yüklendi:', data.categories?.length || 0);
      
      if (data.categories && data.categories.length > 0) {
        // Alt kategoriler var, bunları göster
        setCategories(data.categories);
        setCategoryBreadcrumb([...categoryBreadcrumb, parentCategory]);
        setCurrentParentId(parentCategory.id);
      } else {
        // Alt kategori yok, bu kategoriyi seç
        setSelectedCategory(parentCategory);
        console.log('📄 [N11 CATEGORY SELECTION] Son seviye kategori seçildi:', parentCategory.name);
      }

    } catch (error) {
      console.error('❌ [N11 CATEGORY SELECTION] Alt kategori yükleme hatası:', error);
      setError('Alt kategoriler yüklenirken hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setCategoryLoading(false);
    }
  };

  // Global kategori arama
  const searchCategories = async (searchTerm: string) => {
    if (!companyId) return;

    setCategoryLoading(true);
    try {
      console.log('🔍 [N11 CATEGORY SELECTION] Global kategori arama:', searchTerm);

      const response = await n11Api.post('/api/n11/products/search-categories', { 
        searchTerm,
        parentId: null
      });

      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.error || 'Kategori arama işlemi başarısız');
      }

      console.log('✅ [N11 CATEGORY SELECTION] Arama sonuçları:', data.categories?.length || 0);
      setCategories(data.categories || []);

    } catch (error) {
      console.error('❌ [N11 CATEGORY SELECTION] Kategori arama hatası:', error);
      setError('Kategori arama hatası: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setCategoryLoading(false);
    }
  };

  // Breadcrumb'da geri gitme
  const goBackToParent = (targetIndex: number) => {
    if (targetIndex === -1) {
      // Ana kategorilere dön
      loadMainCategories();
    } else {
      // Belirli bir seviyeye dön
      const targetParent = categoryBreadcrumb[targetIndex];
      loadSubCategories(targetParent);
    }
  };

  // Kategori seçimi onaylama
  const handleConfirm = () => {
    if (selectedCategory) {
      onConfirm(selectedCategory.id, selectedShipmentTemplate || 'BAREM');
    }
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '70vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon />
          <Typography variant="h6">N11 Kategori Seçimi</Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          disabled={loading || categoryLoading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Ürün Bilgisi */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Gönderilecek Ürün:</strong> {item.name}
          </Typography>
          <Typography variant="caption" display="block">
            Stok Kodu: {item.stockCode} | Satış Fiyatı: {item.salePrice.toLocaleString('tr-TR')} ₺
          </Typography>
        </Alert>

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Breadcrumb Navigasyon */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
            📍 Kategori Yolu:
          </Typography>
          <Breadcrumbs 
            aria-label="kategori-breadcrumb" 
            sx={{ 
              fontSize: '0.9rem',
              bgcolor: 'grey.50',
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={() => goBackToParent(-1)}
              sx={{ 
                textDecoration: 'none', 
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' }
              }}
            >
              🏠 Ana Kategoriler
            </Link>
            {categoryBreadcrumb.map((category, index) => (
              category.id.startsWith('path-') ? (
                // Arama ile gelen kategoriler - sadece göster, tıklanamaz
                <Typography 
                  key={category.id}
                  variant="body2" 
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  📁 {category.name}
                </Typography>
              ) : (
                // Normal kategoriler - tıklanabilir
                <Link
                  key={category.id}
                  component="button"
                  variant="body2"
                  onClick={() => goBackToParent(index)}
                  sx={{ 
                    textDecoration: 'none', 
                    cursor: 'pointer',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  📁 {category.name}
                </Link>
              )
            ))}
            {selectedCategory ? (
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                ✅ {selectedCategory.name}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                📂 {categoryBreadcrumb.length > 0 ? 'Alt Kategoriler' : 'Ana Kategoriler'}
              </Typography>
            )}
          </Breadcrumbs>
        </Box>

        {/* Kategori Arama */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Kategori Ara"
            placeholder="En az 2 karakter yazın..."
            value={categorySearchTerm}
            onChange={(e) => setCategorySearchTerm(e.target.value)}
            disabled={loading || categoryLoading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && categorySearchTerm.length >= 2) {
                searchCategories(categorySearchTerm);
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {categoryLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <IconButton
                      onClick={() => {
                        if (categorySearchTerm.length >= 2) {
                          searchCategories(categorySearchTerm);
                        }
                      }}
                      disabled={categorySearchTerm.length < 2 || loading || categoryLoading}
                      size="small"
                    >
                      <SearchIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              // Tüm arama ve seçim durumlarını temizle
              setCategorySearchTerm('');
              setSelectedCategory(null);
              setCategoryBreadcrumb([]);
              setCurrentParentId(null);
              setError(null);
              
              // Ana kategorilere dön
              loadMainCategories();
              
              console.log('🧹 [N11 CATEGORY SELECTION] Tüm seçimler temizlendi, ana kategorilere dönüldü');
            }}
            disabled={loading || categoryLoading}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Temizle
          </Button>
        </Box>

        {/* Kategori Seçimi */}
        <Autocomplete
          fullWidth
          options={categories}
          getOptionLabel={(option) => {
            const hasChildrenIcon = option.hasChildren ? '📁' : '📄';
            return `${hasChildrenIcon} ${option.name}`;
          }}
          value={selectedCategory}
          onChange={(event, newValue) => {
            if (newValue) {
              // Kategori seçildi, alt kategorileri kontrol et
              if (newValue.hasChildren) {
                // Alt kategorisi var, breadcrumb'a ekle ve alt kategorileri yükle
                loadSubCategories(newValue);
              } else {
                // Son seviye kategori, seçili olarak işaretle ve breadcrumb oluştur
                setSelectedCategory(newValue);
                
                // fullPath'den breadcrumb oluştur
                if (newValue.fullPath) {
                  const pathParts = newValue.fullPath.split(' > ');
                  const breadcrumbCategories = pathParts.slice(0, -1).map((name, index) => ({
                    id: `path-${index}`, // Geçici ID
                    name: name,
                    fullPath: pathParts.slice(0, index + 1).join(' > '),
                    level: index,
                    hasChildren: true
                  }));
                  setCategoryBreadcrumb(breadcrumbCategories);
                }
                
                console.log('📄 [N11 CATEGORY SELECTION] Son seviye kategori seçildi:', newValue.name);
                console.log('🍞 [N11 CATEGORY SELECTION] Breadcrumb oluşturuldu:', newValue.fullPath);
              }
            } else {
              setSelectedCategory(null);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={selectedCategory ? `Seçilen: ${selectedCategory.name}` : "N11 Kategorisi Seçin"}
              placeholder={categoryBreadcrumb.length > 0 ? "Alt kategori seçin..." : "Ana kategori seçin..."}
              disabled={loading || categoryLoading}
              helperText={selectedCategory ? `Kategori ID: ${selectedCategory.id}` : "Listeden kategori seçin"}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {categoryLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={loading || categoryLoading}
          loading={categoryLoading}
          noOptionsText="Kategori bulunamadı"
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(x) => x} // Filtrelemeyi backend'e bırak
        />

        {/* Kargo Şablonu Seçimi */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon fontSize="small" />
            🚚 Kargo Şablonu:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="shipment-template-label">Kargo Şablonu Seçin</InputLabel>
            <Select
              labelId="shipment-template-label"
              value={selectedShipmentTemplate}
              onChange={(e) => setSelectedShipmentTemplate(e.target.value)}
              label="Kargo Şablonu Seçin"
              disabled={loading || categoryLoading || templatesLoading}
              startAdornment={
                templatesLoading ? (
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                ) : null
              }
            >
              {/* Kullanıcının kaydettiği şablonlar */}
              {shipmentTemplates.map((template) => (
                <MenuItem key={template.id} value={template.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon fontSize="small" color={template.isDefault ? "primary" : "secondary"} />
                    <Typography>{template.name}</Typography>
                    {template.isDefault && (
                      <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        (Varsayılan)
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
              
              {/* Eğer hiç şablon yoksa varsayılan BAREM seçeneği */}
              {shipmentTemplates.length === 0 && (
                <MenuItem value="BAREM">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon fontSize="small" color="primary" />
                    <Typography>BAREM (Sistem Varsayılanı)</Typography>
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>
          
          {/* Kargo şablonu bilgi mesajı */}
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="caption">
              <strong>💡 Kargo Şablonu Nedir?</strong><br />
              N11 satıcı panelinizdeki "Hesabım &gt; Teslimat Bilgilerim" bölümünden oluşturduğunuz kargo şablon ismidir.
              {shipmentTemplates.length === 0 ? (
                <>
                  <br />
                  <strong>⚠️ Henüz kargo şablonu eklenmemiş.</strong> "Kargo Şablonları" butonundan yeni şablonlar ekleyebilirsiniz. Şimdilik sistem varsayılanı (BAREM) kullanılacak.
                </>
              ) : (
                <>
                  <br />
                  <strong>✅ {shipmentTemplates.length} adet kargo şablonu mevcut.</strong> Varsayılan şablon otomatik seçilmiştir, istediğinizi değiştirebilirsiniz.
                </>
              )}
            </Typography>
          </Alert>
        </Box>

        {/* Yardım Mesajı */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>🌳 Kategori Seçim Rehberi</strong><br />
          1. Ana kategorilerden başlayın<br />
          2. Bir kategori seçtiğinizde alt kategorileri görünür<br />
          3. 📁 Alt kategorisi olan, 📄 Son seviye kategoriler<br />
          4. Breadcrumb ile geri gidebilirsiniz<br />
          <strong>💡 İpucu:</strong> {categoryLoading ? 'Kategoriler yükleniyor...' : `${categories.length} kategori mevcut seviyede`}
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Button
          onClick={onClose}
          disabled={loading || categoryLoading}
          color="inherit"
        >
          İptal
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || categoryLoading || !selectedCategory}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        >
          {loading ? 'Gönderiliyor...' : 'N11\'e Gönder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default N11CategorySelectionModal;
